import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { updateElemental } from '~~/src/api/elementals'
import type { Elemental, ElementalFormData } from '~~/src/types/elemental'
import { ElementalTypeEnum } from '~~/src/types/elemental'

function updateOnSucess(newItem?: Elemental) {
  return (old?: Elemental) => {
    if (!old)
      return
    return newItem
  }
}

export const useUpdateElemental = () => {
  const queryClient = useQueryClient()
  return useMutation<null | {
    'id': string
    'url': string
  }, Error, { elementalType: ElementalTypeEnum; id: string; elemental: Partial<ElementalFormData> },
    {
      previousItem: Elemental | undefined
      newItem: Elemental | undefined
    }>({
      mutationKey: ['update-elemental'],
      mutationFn: async ({ elementalType, id, elemental }) => {
        const route = getElementalTypeRoute(elementalType)
        if (route === 'unknown')
          throw new Error('Unknown elemental type')
        return await updateElemental(`${route}s`, id, elemental)
      },
      onMutate: async ({ elementalType, id, elemental }) => {
        let previousItem, newItem: Elemental | undefined
        const update = (old: Elemental | undefined) => {
          if (!old)
            return
          previousItem = old
          newItem = {
            ...old,
            title: elemental.title,
            description: elemental.description,
            command: { ...old.command, trigger: elemental.command },
            isPrivate: elemental.isPrivate,
            isPremium: elemental.isPremium,
            price: elemental.price,
            priceOriginal: elemental.priceOriginal,
            priceTypeId: elemental.priceTypeId,
            topicIds: elemental.topicIds,
            listIds: elemental.listIds,
            template: elemental.template,
            video: elemental.video,
            tutorialStep: elemental.tutorialStep,
          } as Elemental
          return newItem
        }
        queryClient.setQueriesData({
          queryKey: ['elemental', elementalType, `${id}`],
          exact: false,
          type: 'active',
        }, update)
        if (elemental.url) {
          queryClient.setQueriesData({
            queryKey: ['elemental', elementalType, elemental.url],
            exact: false,
            type: 'active',
          }, update)
        }

        queryClient.setQueriesData({
          queryKey: ['elemental-ownership-data', elemental.url],
          exact: false,
          type: 'all',
        }, (old) => {
          if (!old)
            return old
          return { ...old, template: newItem?.template }
        })

        return {
          previousItem,
          newItem,
        }
      },
      onError: (_, { elementalType, id, elemental }, context) => {
        queryClient.refetchQueries({
          queryKey: ['profile-list-elementals', elementalType],
          exact: false,
          type: 'all',
        })
        queryClient.invalidateQueries({
          queryKey: ['elemental', elementalType, id],
          exact: false,
          type: 'all',
          refetchType: 'none',
        })
        if (elemental.url) {
          queryClient.invalidateQueries({
            queryKey: ['elemental', elementalType, elemental.url],
            exact: false,
            type: 'all',
            refetchType: 'none',
          })
        }
      },
      onSuccess: (result, { elementalType, id, elemental }, { newItem }) => {
        useDispatchCustomEvent('elemental:updated', { element: elemental as Elemental })
        if (elementalType === ElementalTypeEnum.Cell)
          return
        if (result?.url) {
          queryClient.setQueriesData<Elemental | undefined>({
            queryKey: ['elemental', elementalType, result.url],
            exact: false,
            type: 'all',
          }, updateOnSucess({ ...newItem, url: result.url } as Elemental))
          queryClient.refetchQueries({
            queryKey: ['elemental', elementalType, result.url],
            exact: false,
            type: 'all',
          })
          queryClient.setQueriesData({
            queryKey: ['elemental-ownership-data', result.url],
            exact: false,
            type: 'all',
          }, (old) => {
            if (!old)
              return old
            return { ...old, template: newItem?.template }
          })
        }

        queryClient.refetchQueries({
          queryKey: ['profile-list-elementals', elementalType],
          exact: false,
          type: 'all',
        })

        if (elemental.isPremium) {
          queryClient.invalidateQueries({
            queryKey: ['elemental-ownership-data', id],
            exact: false,
            type: 'all',
          })
          queryClient.invalidateQueries({
            queryKey: ['elemental-ownership-data', result?.url],
            exact: false,
            type: 'all',
          })
        }

        if (elemental?.itemIds && elemental.itemIds.length > 0) {
          for (const item of elemental?.itemIds ?? []) {
            if (item.listItemId) {
              queryClient.invalidateQueries({
                queryKey: ['list-elementals', item.listItemId],
                exact: false,
                type: 'all',
              })
            }
          }
        }
        if (elementalType === ElementalTypeEnum.List) {
          queryClient.invalidateQueries({
            queryKey: ['list-elementals', id],
            exact: false,
            type: 'all',
          })
          queryClient.refetchQueries({
            queryKey: ['list-elementals', id],
            exact: false,
            type: 'all',
          })
          queryClient.refetchQueries({
            queryKey: ['profile-list-elementals'],
            exact: false,
            type: 'all',
          })
        }
      },
    })
}
